import React, { useEffect, useState, useRef } from "react";
import { SettingsPane } from "./components/SettingsPane/SettingsPane";
import { SettingsContent } from "./components/SettingsPane/SettingsContent";
import { SettingsMenu } from "./components/SettingsPane/SettingsMenu";

import "./Settings.scss";
import { copyProperties } from "./models/utils";

import Logger from "./Logger";
import { showToast } from "./components/Toasts";

import Settings from "./models/Settings";
import { useOnChangeWatcher } from "./models/useOnChangeWatcher";

import { enumUiMode, getMaxUiMode, isUiModeExpert } from "./uiMode";
import { itx } from "./translationItx/itx";

import { isDevelopmentEnvironment } from "./estada-config";
import GlobalWaitIndicator from "./components/GlobalWaitIndicator";

import SettingPageGeneral from "./settingPages/SettingPageGeneral";
import SettingPageNavigator from "./settingPages/SettingPageNavigator";
import SettingPageRoomplans from "./settingPages/SettingPageRoomplans";
import SettingPageGroupAdresses from "./settingPages/SettingPageGroupAdresses";
import SettingPageDarstellung from "./settingPages/SettingPageDarstellung";
import SettingPageDevelopment from "./settingPages/SettingPageDevelopment";
import SettingPageLogging from "./settingPages/SettingPageLogging";
import SettingPagePerformance from "./settingPages/SettingPagePerformance";
import SettingPageGPT from "./settingPages/SettingPageGPT";
import SettingPageSystem from "./settingPages/SettingPageSystem";
import SettingPageEditProject from "./settingPages/SettingPageEditProject";

// https://github.com/dstuecken/react-settings-pane
const logger = new Logger();

export default function SettingsScreen(props: { defaultPane?: string }): JSX.Element {
  useOnChangeWatcher(state.settings, property => property === "language");
  useOnChangeWatcher(state.projectMetadata, property => property === "isC1Project");
  const [refresh, setRefresh] = useState(1);

  const [oldSettings, setOldSettings] = useState<Settings>();
  const dropDownColor = "#666"; //"var(--dropdown-color)";
  const defaultPane = props.defaultPane ?? "general";

  useEffect(() => {
    const xOldSetting = new Settings();
    copyProperties(state.settings, xOldSetting);
    setOldSettings(xOldSetting);
  }, []);

  // Define your menu
  const menu = [
    {
      title: itx("SettingsScreen.general", "Allgemein"), // Title that is displayed as text in the menu
      url: "/settings/general", // Identifier (url-slug)  // no itx
    },
    {
      title: itx("SettingsScreen.navigator", "Navigator"),
      url: "/settings/navigator", // no itx
    },
    {
      title: itx("SettingsScreen.identifiers", "Kennzeichen"),
      url: "/settings/darstellung", // no itx
    },
    {
      title: itx("SettingsScreen.roomplans", "Raumpläne"),
      url: "/settings/roomplans", // no itx
    },
    {
      title: itx("SettingsScreen.knxAdresses", "KNX Adressen"),
      url: "/settings/groupAddresses", // no itx
    },
    {
      title: itx("SettingsScreen.editProject", "Projekt verwalten"),
      url: "/settings/editProject", // no itx
    },
  ];

  if (isDevelopmentEnvironment()) {
    menu.push({
      title: itx("SettingsScreen.development", "Development"),
      url: "/settings/development", // no itx
    });
    menu.push({
      title: itx("SettingsScreen.logging", "Logging"),
      url: "/settings/logging", // no itx
    });
    menu.push({
      title: itx("SettingsScreen.gpt"),
      url: "/settings/gpt", // no itx
    });
    menu.push({
      title: itx("SettingsScreen.performance", "Performance"),
      url: "/settings/performance", // no itx
    });
  }
  if (isUiModeExpert()) {
    menu.push({
      title: itx("SettingsScreen.system"),
      url: "/settings/system", // no itx
    });
  }

  const customStyles = {
    control: (base: any) => ({
      ...base,
      height: 30,
      minHeight: 30,
      maxHeight: 30,
      containIntrinsicHeight: 30,
    }),
    indicatorContainer: (base: any) => ({
      ...base,
      padding: "0",
    }),
  };

  const customThemes = (theme: any) => ({
    ...theme,
    borderRadius: 3,
    colors: {
      ...theme.colors,
      primary50: "darkgrey", // no itx
      primary25: "lightgrey", // no itx
      primary: "grey", // no itx
    },
  });

  const userRoleModes = [
    {
      value: enumUiMode.user,
      label: itx("SettingsScreen.user", "Anwender"),
    },
    {
      value: enumUiMode.expert,
      label: itx("SettingsScreen.expert", "Experte"),
    },
  ];

  if (getMaxUiMode() >= enumUiMode.productAdmin || isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.productAdmin,
      label: itx("SettingsScreen.productAdmin", "Product Administrator"),
    });
  }

  if (getMaxUiMode() >= enumUiMode.admin || isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.admin,
      label: itx("SettingsScreen.admin", "Administrator"),
    });
  }

  if (isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.developer,
      label: itx("SettingsScreen.developer", "Entwickler"),
    });
  }

  // Return your Settings Pane
  return (
    <div className="app-settings settings-main" style={{ height: "70vh", width: "70vw", overflow: "hidden", margin: "-20px" }}>
      <SettingsPane items={menu} index={`/settings/${defaultPane}`} settings={oldSettings}>
        <SettingsMenu headline={""} />

        <SettingsContent
          closeButtonClass="secondary" // no itx
          saveButtonClass="primary" // no itx
          header={true}
        >
          <SettingPageGeneral
            handler="/settings/general" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />
          <SettingPageNavigator
            handler="/settings/navigator" // no itx
            setRefresh={setRefresh}
            refresh={refresh}
          />
          <SettingPageRoomplans
            handler="/settings/roomplans" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPageGroupAdresses
            handler="/settings/groupAddresses" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />
          <SettingPageDarstellung
            handler="/settings/darstellung" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPageDevelopment
            handler="/settings/development" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPageLogging
            handler="/settings/logging" // no itx
            dropDownColor={dropDownColor}
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPagePerformance
            handler="/settings/performance" // no itx
          />

          <SettingPageGPT
            handler="/settings/gpt" // no itx
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPageEditProject
            handler="/settings/editProject" // no itx
            setRefresh={setRefresh}
            refresh={refresh}
          />

          <SettingPageSystem
            handler="/settings/system" // no itx
          />
        </SettingsContent>
      </SettingsPane>
    </div>
  );
}

export function ImageFieldSettings(props: {
  name: string;
  label: string;
  setRefresh?: (number: number) => void;
  refresh?: number;
}) {
  const { name, label, setRefresh, refresh } = props;
  const xref = useRef<HTMLImageElement>(null);
  const [imageEmpty, setImageEmpty] = useState(!(state as any).settings[name]);
  // if name== "companyLogo"-> only checkIfProfessionalAbo change allowed
  const isEditPossible = name != "companyLogo" || state.projectLicense?.checkIfProfessionalAbo();
  function setImgToTransparent(): React.ReactEventHandler<HTMLImageElement> | undefined {
    let imgElement = document.getElementById(`image_${label}`);
    if (imgElement?.style) {
      imgElement.style.display = "none";
      setImageEmpty(true);
    }
    return undefined;
  }
  function setImgToNotTransparent(): React.ReactEventHandler<HTMLImageElement> | undefined {
    let imgElement = document.getElementById(`image_${label}`);
    if (imgElement?.style) {
      imgElement.style.display = "inline"; // no itx
      setImageEmpty(false);
    }
    return undefined;
  }

  const handleImageUpload = async (ev: any) => {
    function readImagesFileAsBase64Async(file: any): Promise<string> {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();

        reader.onload = () => {
          resolve(reader.result as string);
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
    if (ev.target.files && ev.target.files.length > 0) {
      const file = ev.target.files[0];
      const img = await readImagesFileAsBase64Async(file);
      if (img.startsWith("data:image")) {
        (state as any).settings[name] = img;
        if (name == "companyLogo") {
          state.settings.calcCompanyLogoSize();
        }
        if (setRefresh && refresh) {
          setRefresh(refresh + 1);
        }
        xref.current?.setAttribute("src", img || ""); // no itx
        setImgToNotTransparent();
      } else {
        showToast({
          text: itx("SettingsScreen.noPictureSelected", "leider haben Sie keine Bilddatei ausgewählt"),
        });
      }
      ev.target.value = "";
    }
  };

  const handlePasteFromClipboard = async () => {
    async function imageFromClipboardAsBase64(): Promise<string | undefined> {
      try {
        logger.log("onPasteBackground");
        const clipboardItems = await navigator.clipboard.read();
        if (!clipboardItems || clipboardItems.length == 0) {
          return undefined;
        }

        const firstItem = clipboardItems[0];

        for (const type of firstItem.types) {
          if (type == "image/png" || type == "image/jpg" || type == "image/jpeg") {
            const blob = await clipboardItems[0].getType(type);

            const base64data = await readBlobAsync(blob);
            return base64data;
          }
        }
        return undefined;
      } catch (ex) {
        logger.error(ex);
      }
    }
    function readBlobAsync(blob: Blob): Promise<string> {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();

        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          var base64data = reader.result;
          if (typeof base64data == "string") {
            // logger.log(base64data);
            resolve(base64data);
          }
        };

        reader.onerror = reject;
      });
    }
    const img = await imageFromClipboardAsBase64();
    if (img && img.startsWith("data:image")) {
      state.settings.companyLogo = img;
      state.settings.calcCompanyLogoSize();

      if (setRefresh && refresh) {
        setRefresh(refresh + 1);
      }
      xref.current?.setAttribute("src", img || ""); // no itx
      setImgToNotTransparent();
    } else {
      showToast({
        text: itx("step3.noPictureInClipboard", "Die Zwischenablage enthät kein Bild"),
      });
    }
  };

  const handleDeleteImage = () => {
    state.settings.companyLogo = "";
    state.settings.loadDefaultCompanyLogo();
    state.settings.calcCompanyLogoSize();

    if (setRefresh && refresh) {
      setRefresh(refresh + 1);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "auto",
        paddingTop: "10px",
      }}
    >
      <div className="form-label-bold">{`${label}:`}</div>
      <div className="user-menu-img" style={{ textAlign: "center", display: "flex", justifyContent: "center" }}>
        {
          <img
            ref={xref}
            // className="item-shadow item-fade"
            id={`image_${label}`}
            style={{
              height: "110px",
              objectFit: "contain",
              boxShadow: "var(--panel-shadow)",
              borderRadius: "var(--panel-border-radius)",
              marginRight: "10px",
            }}
            src={state.settings.companyLogoOrDefault}
            onError={() => {
              setImgToTransparent();
            }}
          />
        }

        <div
          style={{
            display: "unset",
            flexDirection: "row",
            textAlign: "center",
          }}
        >
          <button
            style={{
              fontSize: "20px",
              lineHeight: "initial",
            }}
            type="button" // no itx
            className="button"
            id="deleteImg"
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={
              isEditPossible
                ? itx("SettingsScreen.deleteT1", "delete T1", { T1: label })
                : itx("SettingsScreen.deleteT1OnlyWithProAbo", "löschen T1 nur mit Abo möglich", { T1: label })
            }
            data-tooltip-place="bottom"
            onClick={handleDeleteImage}
            disabled={!isEditPossible}
          >
            <i
              className={"las la-trash"} // no itx
            ></i>
          </button>

          <button
            style={{
              fontSize: "20px",
              lineHeight: "initial",
            }}
            type="button" // no itx
            className="button"
            id="pasteFromClipboard"
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={
              isEditPossible
                ? itx("SettingsScreen.pasteImgFromClipboard", "Paste from Clipboard")
                : itx("SettingsScreen.pasteImgFromClipboardOnlyWithProAbo", "Einfügen nur mit Abo möglich")
            }
            data-tooltip-place="bottom"
            onClick={handlePasteFromClipboard}
            disabled={!isEditPossible}
          >
            <i
              className={"las la-paste"} // no itx
            ></i>
          </button>

          <button
            type="button" // no itx
            className="button"
            style={{
              fontSize: "20px",
              display: "none",
            }}
            onClick={() => {
              document.getElementById("imgFromFile")?.click();
            }}
            disabled={!isEditPossible}

            // data-tooltip-id="tv-tooltip"
            // data-tooltip-content={itx("SettingsScreen.changeLogo", "Update Logo")}
            // data-tooltip-place="bottom"
          >
            <i className="las la-upload" style={{ fontSize: "24px" }}></i>
            {itx("SettingsScreen.changeLogo", "Update Logo")}
          </button>

          {/* dummy input for file upload */}
          <input
            style={{ display: "none" }}
            id="imgFromFile"
            type={"file"}
            accept=".jpg,.jpeg,.png" // no itx
            multiple={false}
            placeholder={label}
            onChange={handleImageUpload}
          />

          <button
            style={{
              fontSize: "20px",
              lineHeight: "initial",
            }}
            type="button" // no itx
            className="button"
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={
              isEditPossible
                ? itx("SettingsScreen.changeLogo", "Update Logo")
                : itx("SettingsScreen.changeLogoOnlyWithProAbo", "Update Logo nur mit Abo möglich")
            }
            data-tooltip-place="bottom"
            onClick={() => {
              document.getElementById("imgFromFile")?.click();
            }}
            disabled={!isEditPossible}
          >
            <i className={"las la-upload"} />
          </button>
        </div>
      </div>

      <GlobalWaitIndicator />
    </div>
  );
}

export function TextFieldSettings(props: {
  name: string;
  label: string;
  width?: string;
  setRefresh?: (number: number) => void;
  refresh?: number;
}) {
  const { label, width, setRefresh, refresh } = props;
  var { name } = props;
  const orgName = name;

  const ss = name.split(".");
  name = ss[0];

  const name1 = ss[1];

  return (
    <fieldset
      className="form-group"
      key={orgName + "_texField"} //no itx
    >
      <label
        className="form-label-bold"
        htmlFor="profileName" // no itx
        key={name + "_texField_label"} // no itx
      >
        {`${label}:`}{" "}
      </label>
      <input
        style={{
          marginTop: "10px",
          borderColor: "var(--border-color)",
          width: width || "200px", // no itx
        }}
        key={name + "_texField_input"} // no itx
        type="text"
        className="input"
        name={name}
        placeholder={label}
        id={name}
        onChange={(ev) => {
          if (name1) {
            (state.settings as any)[name][name1] = ev.target.value;
          } else {
            (state.settings as any)[name] = ev.target.value;
          }
          if (setRefresh && refresh) {
            setRefresh(refresh + 1);
          }
        }}
        defaultValue={name1 ? (state.settings as any)[name][name1] : (state.settings as any)[name]}
      />
    </fieldset>
  );
}

export function CheckboxFieldSettings(props: {
  name: string;
  label: string;
  onChange?: (newValue: boolean) => void;
  valueFromProject?: boolean;
  setRefresh?: (number: number) => void;
  refresh?: number;
}) {
  const { label, onChange, valueFromProject, setRefresh, refresh } = props;
  var { name } = props;
  const orgName = name;

  const ss = name.split(".");
  name = ss[0];

  const name1 = ss[1];

  let initialValue;

  if (valueFromProject) {
    initialValue = name1 ? (state.projectMetadata as any)[name][name1] : (state.projectMetadata as any)[name];
  } else {
    initialValue = name1 ? (state.settings as any)[name][name1] : (state.settings as any)[name];
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "auto",
        paddingTop: "10px",
        alignItems: "center",
      }}
      className="form-group"
      key={orgName + "_checkbox"} // no itx
    >
      <input
        key={name + refresh}
        style={{ height: "1em", marginRight: "10px" }}
        type="checkbox"
        name={name}
        placeholder={label}
        id={name}
        onChange={(ev) => {
          if (valueFromProject) {
            if (name1) {
              (state.projectMetadata as any)[name][name1] = ev.target.checked;
            } else {
              (state.projectMetadata as any)[name] = ev.target.checked;
            }
          } else {
            if (name1) {
              (state.settings as any)[name][name1] = ev.target.checked;
            } else {
              (state.settings as any)[name] = ev.target.checked;
            }
          }
          if (setRefresh && refresh) {
            setRefresh(refresh + 1);
          }
          if (onChange) {
            onChange(ev.target.checked);
          }
        }}
        defaultChecked={initialValue}
      />
      <div
        key={name + "_label"} // no itx
      >
        {label}{" "}
      </div>
    </div>
  );
}
